import { useState, useCallback } from 'react';

import { SELECTION_TYPES, SelectionType } from '../constants';
import { SelectedItem } from '../types';

export const useItemSelection = (onSelectionChange?: (selectedItems: SelectedItem[]) => void) => {
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  const removeItem = useCallback(
    (value: string) => {
      const updated = selectedItems.filter((item) => item.value !== value);
      setSelectedItems(updated);
      onSelectionChange?.(updated);
    },
    [selectedItems, onSelectionChange],
  );

  const changeItemType = useCallback(
    (value: string, newType: SelectionType) => {
      const updated = selectedItems.map((item) =>
        item.value === value ? { ...item, type: newType } : item,
      );
      setSelectedItems(updated);
      onSelectionChange?.(updated);
    },
    [selectedItems, onSelectionChange],
  );

  const addItem = useCallback(
    (value: string, group?: string, type?: SelectionType, label?: string) => {
      const newItem: SelectedItem = {
        value,
        label: label || value,
        type: type || SELECTION_TYPES.INCLUDE,
        group,
      };
      const updated = [...selectedItems, newItem];
      setSelectedItems(updated);
      onSelectionChange?.(updated);
    },
    [selectedItems, onSelectionChange],
  );

  const handleItemSelect = useCallback(
    (value: string, group?: string, type?: SelectionType, label?: string) => {
      const existingIndex = selectedItems.findIndex((item) => item.value === value);

      if (existingIndex >= 0) {
        const existing = selectedItems[existingIndex];
        if (type && existing.type === type) {
          removeItem(value);
        } else if (type) {
          changeItemType(value, type);
        }
      } else {
        addItem(value, group, type, label);
      }
    },
    [selectedItems, removeItem, changeItemType, addItem],
  );

  return {
    selectedItems,
    handleItemSelect,
    removeItem,
  };
};
